/* eslint-disable no-shadow */

import Vue from 'vue';
// eslint-disable-next-line import/no-cycle
import ApiService from '@/services/api.service';
import { apiEndpoint } from '@/shared';
import { ValidationError, PMSError, HttpError } from '@/errors';

const endpoint = `${apiEndpoint}/inventory/products`;

const state = {
  data: null,
  pending: false,
  error: null,
  pmsError: null,
  updatePending: false,
  updateError: null,
  validationError: null,
};

const getters = {
  loaded: (state) => !state.pending && state.data != null,
  plans: (state, getters) => (getters.loaded ? state.data?.products?.content : []),
  productPagination: (state, getters) => (getters.loaded ? state.data?.products : []),
  allProducts: (state, getters) => (getters.loaded ? state.data?.allProducts : []),
  contractPlans: (state, getters) => (getters.loaded ? state.data.contractPlans : []),
  rooms: (state, getters) => (getters.loaded ? state.data?.sellable_units?.rooms : []),
  meals: (state, getters) => (getters.loaded ? state.data?.sellable_units?.meals : []),
  extras: (state, getters) => (getters.loaded ? state.data?.sellable_units?.extras : []),
  cancels: (state, getters) => (getters.loaded ? state.data.policies.cancellation : []),
  payment: (state, getters) => (getters.loaded ? state.data.policies.payment : []),
  noShows: (state, getters) => (getters.loaded ? state.data.policies.noshow : []),
  bgarants: (state, getters) => (getters.loaded ? state.data.bgarants : []),
  policies: (state, getters) => (getters.loaded ? state.data.policies : []),
  channels: (state, getters) => (getters.loaded ? state.data.connected_channels : []),
  activeChannels: (state, getters) => (getters.loaded ? state.data.connected_channels : []),
  noPlans: (state, getters) => getters.loaded && !getters?.plans?.length,
  validationError: (state) => state.validationError,
  pmts: (state, getters) => (getters.loaded ? state.data.pmts : []),
};

const mutations = {
  clearErrors(state) {
    state.error = null;
    state.updateError = null;
    state.validationError = null;
  },
  beforeLoading(state, initial = false) {
    if (initial) {
      state.data = null;
    }
    state.error = null;
    state.pending = true;
    state.validationError = null;
  },
  beforeUpdate(state) {
    state.updateError = null;
    state.updatePending = true;
    state.validationError = null;
  },
  error(state, error) {
    state.pending = false;
    state.error = error;
    state.pmsError = error;
  },
  modalError(state, error) {
    state.pending = false;
    state.error = error;
  },
  data(state, data) {
    state.pending = false;
    state.data = data;
  },
  updateContractPlan(state, contractPlans) {
    state.pending = false;
    state.data.contractPlans = contractPlans;
  },
  update(state, error = null) {
    state.updatePending = false;
    state.updateError = error;
  },
  created(state) {
    // state.data.plans.push(JSON.parse(JSON.stringify(plan)));
    state.updatePending = false;
    state.updateError = null;
  },
  updatePolicies(state, data) {
    state.data.cancels = data.cancels;
    state.data.policies = data.policies;
    state.data.noShows = data.noShows;
    state.data.pmts = data.pmts;
  },
  updateMetaData(state, data) {
    state.pending = false;
    state.data = {};
    state.data.allProducts = data.allProducts;
    state.data.sellable_units = data.sellable_units;
    state.data.policies = data.policies;
    state.data.bgarants = data.bgarants;
    state.data.connected_channels = data.connected_channels;
    state.data.pmts = data.pmts;
  },
  updatePlan(state, data) {
    const idx = state.data.plans.findIndex((p) => p.id === data.plans.id);
    if (idx !== -1) {
      Vue.set(state.data.plans, idx, JSON.parse(JSON.stringify(data.plans)));
    }
  },
  modified(state) {
    // const idx = state.data.products.findIndex((p) => p.id === plan.id);
    // if (idx !== -1) {
    //   Vue.set(state.data.products, idx, JSON.parse(JSON.stringify(plan)));
    // }
    state.updatePending = false;
    state.updateError = null;
  },
  deleted(state, id) {
    const idx = state.data?.products?.content.findIndex((plan) => Number(plan.id) === id);
    if (idx !== -1) {
      // eslint-disable-next-line no-unused-expressions
      state.data?.products?.content.splice(idx, 1);
    }
    state.updatePending = false;
    state.updateError = null;
  },
  validationError(state, error) {
    state.validationError = error;
    state.pending = false;
  },
};

function handleApiError(commit, error) {
  const { status, data } = error.response;
  let err = error;
  switch (status) {
    case 400: // BadRequest
      err = new HttpError(status, data.message);
      break;
    case 409: // PMSError
      err = new PMSError(status, data.message, data.errors);
      commit('validationError', err);
      break;
    case 422:
      err = new ValidationError(status, data.message, data.errors);
      commit('validationError', err);
      break;
    case 500:
      err = new Error(data.message);
      break;
    default:
      break;
  }

  // commit('update', err);
  throw err;
}

const actions = {
  async fetchData({ commit }, payload, forced = false) {
    commit('beforeLoading', forced);
    try {
      const page = payload?.page || 1;
      const perPage = payload?.perPage || 10;
      const filter = payload?.filter || '';
      const order = payload?.order || false;
      const sort = payload?.sort || 'id';
      const excludeExpiredProducts = payload?.excludeExpiredProducts || false;
      const filters = payload?.filters || [];
      const dateFilters = payload?.dateFilters || [];

      const query = {
        page, perPage, filter, order, sort, excludeExpiredProducts,
      };
      const queryParams = new URLSearchParams(query).toString();

      const { data } = await ApiService.post(`${endpoint}/all?${queryParams}`, { filters, dateFilters });

      commit('data', data);
      commit('updatePolicies', data);
      commit('updateContractPlan', data.contractPlans);

      return data;
    } catch (error) {
      commit('error', error);
      return [];
    }
  },
  async createPlan({ commit }, plan) {
    commit('beforeUpdate');
    try {
      // const { data } = await ApiService.post(`${endpoint}`, { ...plan });
      await ApiService.post(`${endpoint}`, { ...plan });
      // commit('created', data);
      commit('created');
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async updatePlan({ commit }, plan) {
    commit('beforeUpdate');
    try {
      await ApiService.put(`${endpoint}/${plan.id}`, { ...plan });
      // commit('modified', plan);
      commit('modified');
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async duplicatePlan({ commit }, payload) {
    commit('beforeUpdate');
    try {
      await ApiService.post(`${endpoint}/duplicate`, payload);
      commit('created');
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async deletePlan({ commit }, payload) {
    const { id, count } = payload;
    commit('beforeUpdate');
    try {
      await ApiService.delete(`${endpoint}`, { data: { id, cultswitch_id: payload.cultswitch_id } });
      if (count > 0) {
        commit('deleted', id);
      } else {
        commit('update', null);
      }
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async getProduct({ commit }, { id, byId }) {
    commit('beforeUpdate');
    try {
      const { data } = await ApiService.get(`${endpoint}/${id}/${byId}`);
      commit('modified');
      return data;
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async getMetaData({ commit }, payload, forced = false) {
    commit('beforeLoading', forced);
    try {
      const { data } = await ApiService.get(`${endpoint}/meta`);
      commit('updateMetaData', data);
      return data;
    } catch (error) {
      commit('error', error);
      return [];
    }
  },
  async quickUpdate({ commit }, data) {
    commit('beforeUpdate');
    try {
      await ApiService.post(`${endpoint}/quick-update`, { ...data });
      commit('created');
    } catch (error) {
      commit('error', error);
      throw handleApiError(commit, error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
